'use es6';

import { jsxs as _jsxs } from "react/jsx-runtime";
import { jsx as _jsx } from "react/jsx-runtime";
import { useCallback, useMemo, Fragment } from 'react';
import PropTypes from 'prop-types';
import { WEB_FONT_DEMI_BOLD_WEIGHT } from 'HubStyleTokens/misc';
import I18n from 'I18n';
import FormattedMessage from 'I18n/components/FormattedMessage';
import HR from 'UIComponents/elements/HR';
import UIGrid from 'UIComponents/grid/UIGrid';
import UIGridItem from 'UIComponents/grid/UIGridItem';
import * as LIST_QUERIES from 'marketplace-ui-reviews/constants/ReviewsListQueries';
import UISelect from 'UIComponents/input/UISelect';
import ReviewStats from 'marketplace-ui-reviews/records/ReviewStats';
import ReviewFilters from 'marketplace-ui-reviews/records/ReviewFilters';
import LegacyReviewStats from '../../records/ReviewStats';
import LegacyReviewFilters from '../../records/ReviewFilters';
import { getIndustryOptions, getEmployeesOptions } from '../../utils/reviews';
import { useResponsiveProps } from '../../hooks/useResponsiveProps';
const textPath = 'ecosystemMarketplaceCore.reviews.list';

const ValueComponent = ({
  children
}) => /*#__PURE__*/_jsx("span", {
  role: "button",
  children: children
});
/** @deprecated use ReviewsFilterSortDropdowns.tsx in marketplace-ui-reviews once your app is using the records from marketplace-ui-reviews */


const ReviewsFilterSortDropdowns = ({
  excludedFilterOptions,
  onUpdateFilters,
  reviewFilters,
  reviewStats,
  showDivider = true
}) => {
  const {
    FILTER_KEY,
    INDUSTRY_KEY,
    EMPLOYEES_KEY,
    SORT_KEY
  } = LIST_QUERIES;
  const {
    buttonUse,
    className,
    display,
    textAlign
  } = useResponsiveProps({
    buttonUse: {
      xs: 'link',
      lg: 'transparent'
    },
    className: {
      xs: 'm-left-4',
      lg: 'm-left-0'
    },
    display: {
      xs: 'block',
      md: 'inherit'
    },
    textAlign: {
      xs: 'left',
      xl: 'right'
    }
  });
  const validFilters = useMemo(() => reviewStats.getValidRatingFilters(), [reviewStats]);
  const buildDropdown = useCallback(textKey => {
    const options = Object.keys(LIST_QUERIES[textKey]).reduce((acc, option) => {
      if (!excludedFilterOptions || !excludedFilterOptions.includes(option)) {
        acc.push({
          text: I18n.text(`${textPath}.${textKey}.${option}`),
          value: option,
          disabled: textKey === FILTER_KEY && option !== LIST_QUERIES.FILTER.ALL && !validFilters.has(option)
        });
      }

      return acc;
    }, []);
    return /*#__PURE__*/_jsxs("span", {
      children: [/*#__PURE__*/_jsx("span", {
        style: {
          fontWeight: WEB_FONT_DEMI_BOLD_WEIGHT,
          display
        },
        children: /*#__PURE__*/_jsx(FormattedMessage, {
          message: `${textPath}.${textKey}.label`
        })
      }), /*#__PURE__*/_jsx(UISelect, {
        "data-test-id": `${textKey}-reviews-list-select`,
        className: className,
        buttonUse: buttonUse,
        menuWidth: 250,
        options: options,
        onChange: ({
          target: {
            value
          }
        }) => onUpdateFilters({
          textKey,
          value,
          source: 'dropdown filter'
        }),
        value: reviewFilters.get(textKey.toLowerCase()),
        valueComponent: ValueComponent
      }, textKey)]
    });
  }, [buttonUse, className, display, excludedFilterOptions, onUpdateFilters, FILTER_KEY, reviewFilters, validFilters]);
  const renderedFilterDropdown = buildDropdown(FILTER_KEY);
  const renderedSortDropdown = buildDropdown(SORT_KEY);
  const industryOptions = useMemo(getIndustryOptions, []);

  const renderedIndustryDropdown = /*#__PURE__*/_jsx(UISelect, {
    "data-test-id": "industry-reviews-list-select",
    className: className,
    buttonUse: buttonUse,
    clearable: true,
    menuWidth: 250,
    options: industryOptions,
    onChange: ({
      target: {
        value
      }
    }) => onUpdateFilters({
      textKey: INDUSTRY_KEY,
      value,
      source: 'dropdown filter'
    }),
    placeholder: /*#__PURE__*/_jsx(FormattedMessage, {
      message: "ecosystemMarketplaceCore.reviews.industry.placeholder"
    }),
    value: reviewFilters.get(INDUSTRY_KEY.toLowerCase()),
    valueComponent: ValueComponent
  }, INDUSTRY_KEY);

  const employeesOptions = useMemo(getEmployeesOptions, []);

  const renderedEmployeesDropdown = /*#__PURE__*/_jsx(UISelect, {
    "data-test-id": "employees-reviews-list-select",
    className: className,
    buttonUse: buttonUse,
    clearable: true,
    menuWidth: 250,
    options: employeesOptions,
    onChange: ({
      target: {
        value
      }
    }) => onUpdateFilters({
      textKey: EMPLOYEES_KEY,
      value,
      source: 'dropdown filter'
    }),
    placeholder: /*#__PURE__*/_jsx(FormattedMessage, {
      message: "ecosystemMarketplaceCore.reviews.employees.placeholder"
    }),
    value: reviewFilters.get(EMPLOYEES_KEY.toLowerCase()),
    valueComponent: ValueComponent
  }, EMPLOYEES_KEY);

  return /*#__PURE__*/_jsxs(Fragment, {
    children: [/*#__PURE__*/_jsxs(UIGrid, {
      justify: "between",
      children: [/*#__PURE__*/_jsxs(UIGridItem, {
        wrap: "wrap",
        size: {
          xs: 12,
          xl: 9
        },
        children: [renderedFilterDropdown, renderedIndustryDropdown, renderedEmployeesDropdown]
      }), /*#__PURE__*/_jsx(UIGridItem, {
        size: {
          xs: 12,
          xl: 3
        },
        style: {
          textAlign
        },
        children: renderedSortDropdown
      })]
    }), showDivider && /*#__PURE__*/_jsx(HR, {
      distance: "extra-small"
    })]
  });
};

ReviewsFilterSortDropdowns.propTypes = {
  excludedFilterOptions: PropTypes.array,
  onUpdateFilters: PropTypes.func.isRequired,
  reviewFilters: PropTypes.oneOfType([PropTypes.instanceOf(LegacyReviewFilters), PropTypes.instanceOf(ReviewFilters)]).isRequired,
  reviewStats: PropTypes.oneOfType([PropTypes.instanceOf(LegacyReviewStats), PropTypes.instanceOf(ReviewStats)]).isRequired,
  showDivider: PropTypes.bool
};
export default ReviewsFilterSortDropdowns;